import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { BookingPageDraftResponse } from "../contracts/BookingPageDraftResponse";
import { BookingPageImageUploadResponse } from "../contracts/BookingPageImageUploadResponse";
import { BookingPageSettingsResponse } from "../contracts/BookingPageSettingsResponse";
import { DraftBookingPageRequest } from "../contracts/DraftBookingPageRequest";
import { ImproveBookingPageRequest } from "../contracts/ImproveBookingPageRequest";
import { PaginatedTrialRequestResult } from "../contracts/PaginatedTrialRequestResult";
import { TrialRequestResponse, TrialRequestStatus } from "../contracts/TrialRequestResponse";
import { UpdateBookingPageSettingsRequest } from "../contracts/UpdateBookingPageSettingsRequest";

export const fetchTrialRequests = createAsyncThunk<
  PaginatedTrialRequestResult,
  { status?: TrialRequestStatus; page?: number; pageSize?: number },
  { rejectValue: ApiErrorResponse }
>("trialRequests/fetch", async ({ status, page = 1, pageSize = 20 }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PaginatedTrialRequestResult>("/trial-requests", {
      params: { status, page, pageSize },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const fetchPendingTrialRequestCount = createAsyncThunk<
  number,
  void,
  { rejectValue: ApiErrorResponse }
>("trialRequests/fetchPendingCount", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<number>("/trial-requests/pending-count");
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Everything waiting in the Inbox: pending trial requests + unread messages + pending deletion requests. */
export const fetchInboxCount = createAsyncThunk<
  number,
  void,
  { rejectValue: ApiErrorResponse }
>("trialRequests/fetchInboxCount", async (_, { rejectWithValue }) => {
  try {
    const [trials, messages, deletions] = await Promise.all([
      axiosInstance.get<number>("/trial-requests/pending-count"),
      axiosInstance.get<number>("/contact-messages/new-count"),
      axiosInstance.get<number>("/member-deletion-requests/pending-count"),
    ]);
    return trials.data + messages.data + deletions.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const confirmTrialRequest = createAsyncThunk<
  TrialRequestResponse,
  string,
  { rejectValue: ApiErrorResponse }
>("trialRequests/confirm", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<TrialRequestResponse>(`/trial-requests/${id}/confirm`);
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const declineTrialRequest = createAsyncThunk<
  TrialRequestResponse,
  string,
  { rejectValue: ApiErrorResponse }
>("trialRequests/decline", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<TrialRequestResponse>(`/trial-requests/${id}/decline`);
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const deleteTrialRequest = createAsyncThunk<string, string, { rejectValue: ApiErrorResponse }>(
  "trialRequests/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/trial-requests/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const fetchBookingPageSettings = createAsyncThunk<
  BookingPageSettingsResponse,
  void,
  { rejectValue: ApiErrorResponse }
>("trialRequests/fetchBookingPageSettings", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<BookingPageSettingsResponse>("/instructors/booking-page");
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export type BookingPageImageKind = "hero" | "photo";

export const uploadBookingPageImage = createAsyncThunk<
  BookingPageSettingsResponse,
  { kind: BookingPageImageKind; file: File },
  { rejectValue: ApiErrorResponse }
>("trialRequests/uploadBookingPageImage", async ({ kind, file }, { rejectWithValue }) => {
  try {
    const form = new FormData();
    form.append("file", file);
    const response = await axiosInstance.post<BookingPageSettingsResponse>(
      `/instructors/booking-page/images/${kind}`,
      form,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/**
 * Uploads a service picture and returns its public URL — the caller puts it
 * on the service row and persists via updateBookingPageSettings.
 */
export const uploadServiceImage = createAsyncThunk<
  BookingPageImageUploadResponse,
  File,
  { rejectValue: ApiErrorResponse }
>("trialRequests/uploadServiceImage", async (file, { rejectWithValue }) => {
  try {
    const form = new FormData();
    form.append("file", file);
    const response = await axiosInstance.post<BookingPageImageUploadResponse>(
      "/instructors/booking-page/service-images",
      form,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Selects a shared preset gallery image as the hero background. */
export const setHeroPreset = createAsyncThunk<
  BookingPageSettingsResponse,
  string,
  { rejectValue: ApiErrorResponse }
>("trialRequests/setHeroPreset", async (url, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<BookingPageSettingsResponse>(
      "/instructors/booking-page/images/hero/preset",
      { url },
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const removeBookingPageImage = createAsyncThunk<
  BookingPageSettingsResponse,
  BookingPageImageKind,
  { rejectValue: ApiErrorResponse }
>("trialRequests/removeBookingPageImage", async (kind, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete<BookingPageSettingsResponse>(
      `/instructors/booking-page/images/${kind}`,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/**
 * Asks the assistant to draft the page copy from a short description. Returns
 * the draft to the caller (via unwrap) — nothing is saved until the instructor
 * reviews and applies it through updateBookingPageSettings.
 */
export const draftBookingPage = createAsyncThunk<
  BookingPageDraftResponse,
  DraftBookingPageRequest,
  { rejectValue: ApiErrorResponse }
>("trialRequests/draftBookingPage", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<BookingPageDraftResponse>(
      "/assistant/booking-page/draft",
      request,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/**
 * Asks the assistant to polish the page's existing copy (facts unchanged; the
 * current content is read server-side). Returns the improved draft to the
 * caller (via unwrap) — nothing is saved until the instructor applies it.
 */
export const improveBookingPage = createAsyncThunk<
  BookingPageDraftResponse,
  ImproveBookingPageRequest,
  { rejectValue: ApiErrorResponse }
>("trialRequests/improveBookingPage", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<BookingPageDraftResponse>(
      "/assistant/booking-page/improve",
      request,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const updateBookingPageSettings = createAsyncThunk<
  BookingPageSettingsResponse,
  UpdateBookingPageSettingsRequest,
  { rejectValue: ApiErrorResponse }
>("trialRequests/updateBookingPageSettings", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<BookingPageSettingsResponse>(
      "/instructors/booking-page",
      request,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});
